import { match, P } from 'ts-pattern';

import type { StripePaymentType } from '@endaoment-frontend/types';

export const calculateStripeValues = ({
  basePledgeAmountCents,
  offsetFees,
  paymentType,
  endaomentFeeBps,
}: {
  basePledgeAmountCents: number;
  offsetFees: boolean;
  paymentType: StripePaymentType;
  endaomentFeeBps: number;
}): {
  donationAmount: number;
  proceeds: number;
  endaomentFee: number;
  stripeFee: number;
  totalFees: number;
} => {
  const basePledgeAmount = basePledgeAmountCents / 100;
  const endaomentFeeNormalized = endaomentFeeBps / 10_000;

  return match(paymentType)
    .with(P.union('Card', 'Apple Pay', 'Cash App', 'Google Pay'), () => {
      const stripeRate = 0.029;
      const stripeFixed = 0.3;

      // Offset: Solve D such that proceeds B are met with Endaoment fee after Stripe
      // proceeds = (1 - e) * (D * (1 - r) - f) => D = (B / (1 - e) + f) / (1 - r)
      const donationAmount = offsetFees
        ? (basePledgeAmount / (1 - endaomentFeeNormalized) + stripeFixed) / (1 - stripeRate)
        : basePledgeAmount;

      const stripeFee = donationAmount * stripeRate + stripeFixed;
      const netAfterStripe = donationAmount - stripeFee;
      const endaomentFee = Math.max(0, netAfterStripe * endaomentFeeNormalized);
      const totalFees = stripeFee + endaomentFee;
      const proceeds = donationAmount - totalFees;

      return {
        donationAmount,
        proceeds,
        endaomentFee,
        stripeFee,
        totalFees,
      };
    })

    .with('Bank Transfer', () => {
      const stripeRate = 0.008;
      const stripeCap = 5;

      // Offset: Two regimes (percent vs capped)
      const donationAmount = offsetFees
        ? (() => {
            const uncappedD = basePledgeAmount / ((1 - stripeRate) * (1 - endaomentFeeNormalized));
            if (uncappedD * stripeRate <= stripeCap) return uncappedD;
            // Capped: proceeds = (1 - e) * (D - cap) => D = B / (1 - e) + cap
            return basePledgeAmount / (1 - endaomentFeeNormalized) + stripeCap;
          })()
        : basePledgeAmount;

      const stripeFee = Math.min(donationAmount * stripeRate, stripeCap);
      const netAfterStripe = donationAmount - stripeFee;
      const endaomentFee = Math.max(0, netAfterStripe * endaomentFeeNormalized);
      const totalFees = stripeFee + endaomentFee;
      const proceeds = donationAmount - totalFees;

      return {
        donationAmount,
        proceeds,
        endaomentFee,
        stripeFee,
        totalFees,
      };
    })

    .with('Other', () => {
      const stripeRate = 0.03;
      const stripeMin = 15;

      // Offset: Two regimes (percent vs minimum)
      const donationAmount = offsetFees
        ? (() => {
            const percentD = basePledgeAmount / ((1 - stripeRate) * (1 - endaomentFeeNormalized));
            if (percentD * stripeRate >= stripeMin) return percentD;
            // Minimum: proceeds = (1 - e) * (D - min) => D = B / (1 - e) + min
            return basePledgeAmount / (1 - endaomentFeeNormalized) + stripeMin;
          })()
        : basePledgeAmount;

      const stripeFee = Math.max(donationAmount * stripeRate, stripeMin);
      const netAfterStripe = donationAmount - stripeFee;
      const endaomentFee = Math.max(0, netAfterStripe * endaomentFeeNormalized);
      const totalFees = stripeFee + endaomentFee;
      const proceeds = donationAmount - totalFees;

      return {
        donationAmount,
        proceeds,
        endaomentFee,
        stripeFee,
        totalFees,
      };
    })

    .exhaustive();
};

export const convertStripeRawTypeToPaymentType = (rawType: string): StripePaymentType =>
  match<string, StripePaymentType>(rawType)
    // TODO: Handle Stripe link more gracefully (it might be a bank transfer, but we don't technically know which)
    .with(P.union('card', 'link'), () => 'Card')
    .with(
      P.union(
        'us_bank_account',
        'acss_debit',
        'au_becs_debit',
        'bacs_debit',
        'bancontact',
        'eps',
        'fpx',
        'giropay',
        'ideal',
        'p24',
        'pix',
        'sepa_debit',
        'sofort',
      ),
      () => 'Bank Transfer',
    )
    .with('cashapp', () => 'Cash App')
    .with('apple_pay', () => 'Apple Pay')
    .otherwise(() => 'Other');
